/**
 * Send-time merge data for classifying the flight's subject / preheader override (audit round 2,
 * 2026-10-07). Must contain EXACTLY what the send path's template data will hold — nothing the preview
 * adds for its own rendering (current_date / current_year, voucher placeholders, "[content missing…]").
 *
 * Mirrors supabase/functions/_shared/flightSendHelpers.ts renderTemplateData:
 *   custom       -> customValue, with the literal "{{current_date}}" / "{{current_year}}" converted
 *   contact      -> contact[sourceField]; "custom:<slug>" attributes come from a server index -> UNKNOWN here
 *   voucher      -> the recipient's voucher -> UNKNOWN here
 *   content      -> contentUrls[sourceField] (UNKNOWN when the preview has no URL for it)
 *   landing_page -> per-recipient link -> UNKNOWN here
 *   lookup       -> resolveLookupMapping (default_row: contact-independent; keyed: needs the contact)
 *   url          -> customValue with surrounding whitespace trimmed (same conversions as custom) — the
 *                   send renderer gained this case 2026-10-07; before that url values never reached email
 *   empty / null values are omitted (renderTemplateData only sets non-empty values)
 * Standard fields: email always; first_name / last_name when truthy (UNKNOWN without a contact).
 * extraFields: the flight preheader (flightOverrideValue) is in template data before the subject is
 * substituted, so `{{preheader}}` in a subject resolves to it.
 * Not modelled (Event-in-a-Box / test-send only): inviteExtra, runtimeMerge, staticMerge, is_test_send.
 */
import type { FieldMapping } from "../services/templates.service";
import {
  flightOverrideValue,
  isCrmSystemLinkField,
} from "./systemTemplateVariables";

export interface SendTimeMergeData {
  vars: Record<string, unknown>;
  /** Fields whose send-time value depends on data the preview doesn't have. */
  unknown: Set<string>;
}

export function buildSendTimeMergeData(input: {
  mappings: FieldMapping[];
  /** The selected contact, or null when none is selected. */
  contact: Record<string, unknown> | null;
  contentUrls?: Record<string, string>;
  flightPreheader?: string | null;
  /** Same algorithm as the send path's resolveLookupMapping; returns the resolved value ("" = none). */
  resolveLookup?: (m: FieldMapping) => string;
  now?: Date;
}): SendTimeMergeData {
  const { mappings, contact, contentUrls, flightPreheader, resolveLookup } =
    input;
  const now = input.now ?? new Date();
  const vars: Record<string, unknown> = {};
  const unknown = new Set<string>();
  const set = (field: string, v: unknown) => {
    if (v !== undefined && v !== null && v !== "") {
      vars[field] = v;
    }
  };
  for (const m of mappings) {
    const field = m.templateField;
    if (!field || m.sourceType === "sendgrid" || isCrmSystemLinkField(field)) {
      continue;
    }
    switch (m.sourceType) {
      case "custom":
      case "url": {
        let v: unknown =
          m.sourceType === "url" && typeof m.customValue === "string"
            ? m.customValue.trim()
            : m.customValue;
        if (v === "{{current_date}}") {
          v = now.toLocaleDateString();
        } else if (v === "{{current_year}}") {
          v = now.getFullYear().toString();
        }
        set(field, v);
        break;
      }
      case "contact": {
        const sf = m.sourceField ?? "";
        if (!contact || sf.startsWith("custom:")) {
          if (sf) {
            unknown.add(field);
          }
        } else if (sf) {
          set(field, contact[sf]);
        }
        break;
      }
      case "voucher":
      case "landing_page":
        unknown.add(field);
        break;
      case "content": {
        const url = m.sourceField ? contentUrls?.[m.sourceField] : undefined;
        if (url) {
          set(field, url);
        } else {
          unknown.add(field);
        }
        break;
      }
      case "lookup": {
        const contactIndependent = m.lookupResolution === "default_row";
        if (!resolveLookup || (!contact && !contactIndependent)) {
          unknown.add(field);
        } else {
          set(field, resolveLookup(m));
        }
        break;
      }
      default:
        // Any other type: the send renderer supplies nothing.
        break;
    }
  }
  if (contact) {
    set("email", contact.email);
    if (contact.first_name) {
      vars.first_name = contact.first_name;
    }
    if (contact.last_name) {
      vars.last_name = contact.last_name;
    }
  } else {
    for (const f of ["email", "first_name", "last_name"]) {
      unknown.add(f);
    }
  }
  const pre = flightOverrideValue(flightPreheader);
  if (pre !== undefined) {
    vars.preheader = pre;
    unknown.delete("preheader");
  }
  // A known send-time value always beats "unknown" for the same key only when nothing later can replace it;
  // standard fields are applied after mappings in the renderer, so they win over a same-named mapping.
  return { vars, unknown };
}
